import { useState } from 'react';
import { Alert, App, Button, Card, Form, Space, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { CreateCustomerBody } from '@goelectric/shared-types';
import { customerApi } from '../api/customerApi';
import { CustomerForm, type CreateCustomerFormState } from '../components/CustomerForm';
import { describeCustomerApiError } from '../utils/customerErrors';

/**
 * Página de alta de cliente (`/clientes/nuevo`, solo `SUPER_ADMIN`/`SOPORTE_N3` — el
 * guard de la ruta lo garantiza). Compone `CustomerForm` con el POST: en éxito navega
 * al detalle del cliente creado; en error muestra el `message` del backend y marca el
 * campo si el error lo identifica (ADR 0021).
 */
export function CreateCustomerPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm<CreateCustomerFormState>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const goToList = () => navigate('/clientes');

  const handleSubmit = async (body: CreateCustomerBody) => {
    setSubmitting(true);
    setError(null);
    try {
      const created = await customerApi.create(body);
      message.success(`Cliente ${created.id} creado.`);
      navigate(`/clientes/${encodeURIComponent(created.id)}`);
    } catch (caught) {
      const { message: detail, field } = describeCustomerApiError(caught);
      setError(detail);
      if (field) {
        form.setFields([{ name: field as keyof CreateCustomerFormState, errors: [detail] }]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="clientes-page">
      <div className="clientes-page__header">
        <Space align="center">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Volver al listado de clientes"
            onClick={goToList}
          />
          <Typography.Title level={3} style={{ margin: 0 }}>
            Nuevo cliente
          </Typography.Title>
        </Space>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message="No se pudo crear el cliente"
          description={error}
        />
      )}

      <Card>
        <CustomerForm form={form} onSubmit={handleSubmit} submitting={submitting} />
      </Card>
    </div>
  );
}
